import { useState } from 'react';
import NumberInputWithMultiplier from 'src/components/input/NumberInputWithMultiplier';
import Button from '../button/Button';
import IconSvg from 'src/assets/icons/IconSvg';
import RaffleCountdown from 'src/components/common/RaffleCountdown';
import { useViewport } from 'src/context/ViewportContext';
import { satsToBtc } from 'src/utils/raffleHelpers';

interface Props {
  image: string;
  title: string;
  creator: string;
  ticketsSold: string;
  ticketsPrice: number;
  endDate: string;
  onClick?: () => void;
  isFavorited?: boolean;
  onToggleFavorite?: () => void;
  onBuy?: (quantity: number) => void;
  isBuying?: boolean;
  supply?: number;
  ticketsSoldCount?: number;
  ticketLimitPerWallet?: number | null;
  totalTicketValue?: number;
}

const RaffleCard = ({
  image,
  title,
  creator,
  ticketsSold,
  ticketsPrice,
  endDate,
  onClick,
  isFavorited,
  onToggleFavorite,
  onBuy,
  isBuying,
  supply,
  ticketsSoldCount,
  ticketLimitPerWallet,
  totalTicketValue,
}: Props) => {
  const { isMobile } = useViewport();
  const [quantity, setQuantity] = useState(1);

  const remaining = supply != null && ticketsSoldCount != null ? supply - ticketsSoldCount : undefined;
  const isEnded = new Date() >= new Date(endDate);
  const maxQty = Math.min(
    ticketLimitPerWallet ?? Infinity,
    remaining ?? Infinity,
  );
  const totalBtc = (ticketsPrice * quantity).toFixed(8).replace(/\.?0+$/, '');

  const floorPrice = totalTicketValue != null ? satsToBtc(totalTicketValue) : null;
  const percentChange = floorPrice && ticketsPrice > 0
    ? (((floorPrice - ticketsPrice) / ticketsPrice) * 100).toFixed(1)
    : null;

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.();
  };

  const handleBuyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBuy?.(quantity);
  };

  return (
    <div className="space-y-2 w-full">
      <div
        className="h-[408px] rounded-lg relative overflow-hidden"
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <button
          className={`absolute bg-brand-black rounded-lg p-2.5 flex items-center justify-center top-5 right-5 z-10 ${
            isFavorited ? 'text-brand-tangerine' : 'text-grayscale-400'
          }`}
          onClick={handleStarClick}
        >
          <IconSvg icon="starM" />
        </button>

        <img src={image} alt="" className="object-cover w-full h-full" />

        <div className="absolute bottom-3 left-3 right-3 bg-[#10100FAD] rounded p-3 flex items-center gap-2 text-grayscale-300 text-sm backdrop-blur-sm">
          <span>FP: {ticketsPrice} BTC</span>
          <span>|</span>
          <span>TTV: {floorPrice != null ? floorPrice : ticketsPrice} BTC</span>
          {percentChange && (
            <>
              <span>|</span>
              <span className={Number(percentChange) >= 0 ? 'text-system-success' : 'text-system-error'}>
                {Number(percentChange) >= 0 ? '+' : ''}{percentChange}%
              </span>
            </>
          )}
        </div>
      </div>

      <div className="p-5 space-y-4 rounded-md bg-grayscale-800">
        <div
          className="space-y-2"
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        >
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full"></div>
              <div className="font-medium text-brand-granite">{creator}</div>
            </div>

            <RaffleCountdown endDate={endDate} className="text-sm" />
          </div>

          <div className="flex gap-2 items-center">
            <h4 className="text-grayscale-100">{title}</h4>

            <IconSvg icon="verifiedM" className="text-brand-tangerine" />
          </div>
        </div>

        <div className="flex justify-between">
          <div className="space-y-1">
            <div className="text-xs text-brand-granite">Tickets Sold</div>

            <div className="text-lg font-semibold text-grayscale-100">{ticketsSold}</div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-brand-granite">Remaining</div>

            <div className="flex gap-1 justify-center items-center mx-auto text-lg font-semibold text-grayscale-100">
              <span>{remaining ?? '—'}</span>
            </div>
          </div>

          <div className="space-y-1 text-right">
            <div className="text-xs text-brand-granite">Tickets Price</div>

            <div className="flex gap-1 justify-end items-center text-grayscale-100">
              <IconSvg icon="bitcoinS" />

              <div className="text-lg font-semibold text-grayscale-100">{ticketsPrice}</div>
            </div>

            <div className="text-xs text-grayscale-400">≈ ${ticketsPrice}</div>
          </div>
        </div>

        <div className="flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
          <NumberInputWithMultiplier
            className="w-full"
            value={quantity}
            onChange={setQuantity}
            min={1}
            max={maxQty === Infinity ? 100 : maxQty}
          />

          <Button
            variant="primary"
            size={isMobile ? 'xs' : 'sm'}
            onClick={handleBuyClick}
            disabled={isEnded || isBuying || (remaining != null && remaining <= 0)}
          >
            {isEnded ? 'Ended' : isBuying ? 'Buying...' : `Buy • ${totalBtc} BTC`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RaffleCard;
